/**
 * The unified dashboard's composition (#1337), revision 5
 * (`docs/plans/2026-10-02-dashboard-prototype-rev5.md`): every tab is a first
 * layer of 3–4 cards and sections of the rest, reached by anchor chips. A
 * block whose source is not built yet is not drawn: the comment at its place
 * names the issue that builds the source, and that issue adds the block (an
 * empty «скоро» card is not shown to the customer — owner, #758).
 *
 * Each block declares what it needs (`requires`, the capability vocabulary of
 * `@aica/api-types` `access.ts`); a block the viewer cannot have is not
 * drawn, and a tab with no drawable block is not offered.
 */
import type { FeatureFlagKey } from '@aica/core';
import type { FeatureGateState } from '@/contexts/FeatureFlagsContext';
import type { AnalyticsCapability } from '@aica/api-types/schemas';
import { meets, type CapabilityRequirement, type CapabilitySet } from './capabilities';
import {
  BUSINESS_SECTIONS,
  PERSONAL_SECTIONS,
  type UnifiedSection,
  type UnifiedView,
} from './unified-search';

/** The status a card shows (rev 5): none, «скоро», «методика», «превью». */
export type BlockStatus = 'soon' | 'method' | 'preview';

/** The flow slice's cards (#1337, «Разговоры»), one answer behind all of them. */
export const FLOW_BLOCK_KINDS = [
  'flow-received',
  'flow-missed',
  'flow-volume',
  'flow-categories',
  'flow-channels',
  'flow-projects',
  'flow-hours',
  'flow-day-hour',
] as const;
export type FlowBlockKind = (typeof FLOW_BLOCK_KINDS)[number];

/** Whether a block is a flow card — by the list above, so a ninth kind needs no second place. */
export function isFlowBlockKind(kind: BlockKind): kind is FlowBlockKind {
  return (FLOW_BLOCK_KINDS as readonly BlockKind[]).includes(kind);
}

/** The team slice's cards (#1338, «Команда»), one answer behind all of them. */
export const TEAM_BLOCK_KINDS = [
  'team-roster',
  'team-attention',
  'team-growth',
  'team-distribution',
  'team-workload',
] as const;
export type TeamBlockKind = (typeof TEAM_BLOCK_KINDS)[number];

/** Whether a block is a team card — by the list above. */
export function isTeamBlockKind(kind: BlockKind): kind is TeamBlockKind {
  return (TEAM_BLOCK_KINDS as readonly BlockKind[]).includes(kind);
}

/** The sales slice's cards (#1339, «Продажи»), one answer behind all of them. */
export const SALES_BLOCK_KINDS = [
  'sales-outcomes',
  'sales-freshness',
  'sales-outcome-score',
  'sales-stage-advance',
  'sales-depth',
] as const;
export type SalesBlockKind = (typeof SALES_BLOCK_KINDS)[number];

/** Whether a block is a sales card — by the list above. */
export function isSalesBlockKind(kind: BlockKind): kind is SalesBlockKind {
  return (SALES_BLOCK_KINDS as readonly BlockKind[]).includes(kind);
}

/**
 * The voice slice's cards (#1340, «Голос клиента»), one answer behind all of
 * them — each reads one of its sections (`market`, `themes`, `questions`,
 * `barriers`).
 */
export const VOICE_BLOCK_KINDS = [
  'voice-competitors',
  'voice-complaints',
  'voice-market-objections',
  'voice-trends',
  'voice-topics',
  'voice-product-feedback',
  'voice-needs',
  'voice-questions',
  'voice-refusals',
] as const;
export type VoiceBlockKind = (typeof VOICE_BLOCK_KINDS)[number];

/** Whether a block is a voice card — by the list above. */
export function isVoiceBlockKind(kind: BlockKind): kind is VoiceBlockKind {
  return (VOICE_BLOCK_KINDS as readonly BlockKind[]).includes(kind);
}

/**
 * The Quality tab's cards beyond the E1 block (#1338 PR B). The two skills
 * cards read the E1 slice's own answer (`skills`); the rest read the
 * `quality/sections` adapter, one section each. «Attention Score» reads
 * nothing: its status is «методика» and it has no number.
 */
export const QUALITY_SKILL_BLOCK_KINDS = [
  'quality-skills-drop',
  'quality-skills',
] as const;
export const QUALITY_SECTION_BLOCK_KINDS = [
  'quality-review',
  'quality-script',
  'quality-steps',
  'quality-objections',
  'quality-compliance',
  'quality-ai-accuracy',
  'quality-attention',
  'quality-criteria',
] as const;
export type QualitySkillBlockKind = (typeof QUALITY_SKILL_BLOCK_KINDS)[number];
export type QualitySectionBlockKind = (typeof QUALITY_SECTION_BLOCK_KINDS)[number];

/** Whether a block is a skills card — by the list above. */
export function isQualitySkillBlockKind(kind: BlockKind): kind is QualitySkillBlockKind {
  return (QUALITY_SKILL_BLOCK_KINDS as readonly BlockKind[]).includes(kind);
}

/** Whether a block is a card of the sections adapter (or the method card). */
export function isQualitySectionBlockKind(
  kind: BlockKind
): kind is QualitySectionBlockKind {
  return (QUALITY_SECTION_BLOCK_KINDS as readonly BlockKind[]).includes(kind);
}

/**
 * The employee's own cards on «Моё» (#1338 PR C), beyond the E1 score and
 * days: one `/agents/me/performance` answer behind the focus, the position,
 * the skills against the team and the bests; one E1 answer behind the
 * progress; the notes their supervisor left them behind the next, and their
 * coaching meetings in the window behind the last (#2053).
 */
export const PERSONAL_BLOCK_KINDS = [
  'personal-progress',
  'personal-focus',
  'personal-rank',
  'personal-skills',
  'personal-bests',
  'personal-notes',
  'personal-coaching',
] as const;
export type PersonalBlockKind = (typeof PERSONAL_BLOCK_KINDS)[number];

/** Whether a block is a personal card — by the list above. */
export function isPersonalBlockKind(kind: BlockKind): kind is PersonalBlockKind {
  return (PERSONAL_BLOCK_KINDS as readonly BlockKind[]).includes(kind);
}

/** The Summary's own blocks (#1337, PR 3a): projections of what the domains answer. */
export const SUMMARY_BLOCK_KINDS = [
  'summary-analyzed',
  'summary-alerts',
  'summary-headline',
  'summary-by-tab',
  'summary-coverage',
  'summary-recent',
] as const;
export type SummaryBlockKind = (typeof SUMMARY_BLOCK_KINDS)[number];

/**
 * What draws the card: the E1 quality block's pieces, the flow's, the
 * Summary's, or a placeholder.
 */
export type BlockKind =
  | 'quality-score'
  | 'quality-trend'
  | 'quality-projection'
  | FlowBlockKind
  | TeamBlockKind
  | SalesBlockKind
  | VoiceBlockKind
  | QualitySkillBlockKind
  | QualitySectionBlockKind
  | PersonalBlockKind
  | 'coaching-stats'
  /** Частые просадки сотрудника (#767): выбранного в «Команде», свои в «Моё». */
  | 'employee-issues'
  /** Балл по критериям до и после последнего фидбека (#2609): тот же выбор, что у просадок. */
  | 'feedback-effect'
  | SummaryBlockKind
  | 'voice-export'
  | 'soon';

/**
 * Existing big reports in «Аналитика» a card leads to («Отчёт →»). The
 * `voice*` keys are the released report's own tabs and sections
 * (`/analytics/voice`: `audience`, `voiceSection`).
 */
export type ReportKey =
  | 'flow'
  | 'missed'
  | 'voice'
  | 'voiceMarketing'
  | 'voiceProduct'
  | 'voiceThemes'
  | 'voiceQuestions'
  | 'voiceDeclines'
  | 'objections'
  | 'scriptSteps'
  | 'criteria';

/** Existing screens where something is done («Действие →»). */
export type ActionKey =
  'review' | 'coaching' | 'alerts' | 'setup' | 'records' | 'criteria';

export const REPORT_ROUTES: Readonly<Record<ReportKey, string>> = {
  flow: '/analytics/call-stats',
  missed: '/analytics/missed-calls',
  voice: '/analytics/voice',
  voiceMarketing: '/analytics/voice?audience=marketing',
  voiceProduct: '/analytics/voice?audience=product',
  voiceThemes: '/analytics/voice?voiceSection=themes',
  voiceQuestions: '/analytics/voice?voiceSection=questions',
  voiceDeclines: '/analytics/voice?voiceSection=declines',
  objections: '/analytics/objections',
  scriptSteps: '/analytics/script-steps',
  criteria: '/analytics/criteria',
};

export const ACTION_ROUTES: Readonly<Record<ActionKey, string>> = {
  review: '/review-queue',
  coaching: '/coaching/notes',
  alerts: '/alerts',
  setup: '/analysis-setup',
  records: '/records',
  criteria: '/analysis-setup/call-criteria',
};

export interface BlockSpec extends CapabilityRequirement {
  /** The i18n key under `unified.blocks`. */
  id: string;
  kind: BlockKind;
  status?: BlockStatus;
  report?: ReportKey;
  /**
   * What the report shows its reader beyond `requires`: «Отчёт →» is drawn
   * only to a viewer who has all of these, where the card itself is not (the
   * released report keeps its own boundary).
   */
  reportRequires?: readonly AnalyticsCapability[];
  action?: ActionKey;
  /** A feature flag the block needs on: with it off the block is not drawn. */
  flag?: FeatureFlagKey;
}

export interface SectionSpec {
  /** The i18n key under `unified.sections` and the anchor's id. */
  id: string;
  items: readonly BlockSpec[];
}

export interface TabLayout {
  first: readonly BlockSpec[];
  sections: readonly SectionSpec[];
  /**
   * A tab sold from a tier up (#1339): its flag. Below the plan every tab
   * strip draws it locked and the tab opens on the plan screen.
   */
  tierFlag?: FeatureFlagKey;
}

const AGGREGATES: readonly AnalyticsCapability[] = [
  'aggregates.flow',
  'aggregates.sales',
  'aggregates.quality',
  'aggregates.team',
  'aggregates.voc',
];

const FLOW: readonly AnalyticsCapability[] = ['aggregates.flow'];

/** A card of the flow slice — a preview, like the rest of the shell's live blocks. */
function flow(id: string, kind: FlowBlockKind, report: ReportKey): BlockSpec {
  return { id, kind, status: 'preview', requires: FLOW, report };
}
const SALES: readonly AnalyticsCapability[] = ['aggregates.sales'];

/** A card of the sales slice — a preview, like the rest of the shell's live blocks. */
function sales(
  id: string,
  kind: SalesBlockKind,
  extra: Partial<Pick<BlockSpec, 'report'>> = {}
): BlockSpec {
  return { id, kind, status: 'preview', requires: SALES, ...extra };
}
const QUALITY: readonly AnalyticsCapability[] = ['aggregates.quality'];
const TEAM: readonly AnalyticsCapability[] = ['aggregates.team'];
/**
 * The dips of the employee picked in «Сотрудники» (#767): a person's data
 * (`team.scores`) and the quality aggregate the issues belong to — the route's
 * own requirement (`GET /reports/employee-issues`). Without them the names in
 * the roster open nothing.
 */
export const EMPLOYEE_ISSUES_TEAM: readonly AnalyticsCapability[] = [
  'aggregates.team',
  'team.scores',
  'aggregates.quality',
];
const VOC: readonly AnalyticsCapability[] = ['aggregates.voc'];

/** A card of the voice slice — a preview, like the rest of the shell's live blocks. */
function voice(
  id: string,
  kind: VoiceBlockKind,
  report: ReportKey,
  extra: Partial<Pick<BlockSpec, 'reportRequires'>> = {}
): BlockSpec {
  return { id, kind, status: 'preview', requires: VOC, report, ...extra };
}

/**
 * The released report shows customer questions and decline reasons only to
 * whoever reads the customers' words and the records behind them
 * (`projectVoice`); an analyst's card shows the labels and counts, with no
 * way into a report that would show them nothing (owner decision 13.10).
 */
export const VOICE_WORDS: readonly AnalyticsCapability[] = [
  'evidence.text',
  'records.read',
];

/** A card of the Quality tab beyond E1 — a preview, like the rest of the shell's live blocks. */
function quality(
  id: string,
  kind: QualitySkillBlockKind | QualitySectionBlockKind,
  extra: Partial<Pick<BlockSpec, 'report' | 'action' | 'requires' | 'status'>> = {}
): BlockSpec {
  return { id, kind, status: 'preview', requires: QUALITY, ...extra };
}

/** A card of the team slice — a preview, like the rest of the shell's live blocks. */
function team(
  id: string,
  kind: TeamBlockKind,
  extra: Partial<Pick<BlockSpec, 'action'>> = {}
): BlockSpec {
  return { id, kind, status: 'preview', requires: TEAM, ...extra };
}

/**
 * A card of «Моё» — the viewer's own numbers, so it needs no capability: the
 * routes read the caller's own records whatever the role, and the view is
 * offered only to the employee (`unifiedViewOf`).
 */
function personal(
  id: string,
  kind: PersonalBlockKind,
  extra: Partial<Pick<BlockSpec, 'requires' | 'flag'>> = {}
): BlockSpec {
  return { id, kind, status: 'preview', requires: [], ...extra };
}

export const UNIFIED_LAYOUT: Readonly<Record<UnifiedSection, TabLayout>> = {
  summary: {
    first: [
      // A projection of the Quality tab's block — the same E1 answer.
      {
        id: 'qualityProjection',
        kind: 'quality-projection',
        status: 'preview',
        requires: QUALITY,
      },
      { id: 'analyzed', kind: 'summary-analyzed', status: 'preview', requires: FLOW },
      {
        id: 'alerts',
        kind: 'summary-alerts',
        status: 'preview',
        requires: QUALITY,
        action: 'alerts',
      },
      // Only some domains have theses: Flow, Quality and Voice of Customer (the
      // most named competitor); Sales and Team add none (and a viewer with only
      // those has no card to draw).
      {
        id: 'headline',
        kind: 'summary-headline',
        status: 'preview',
        requires: [],
        requiresAny: ['aggregates.flow', 'aggregates.quality', 'aggregates.voc'],
      },
    ],
    sections: [
      {
        id: 'byTab',
        items: [
          {
            id: 'byTab',
            kind: 'summary-by-tab',
            status: 'preview',
            requires: [],
            requiresAny: AGGREGATES,
          },
        ],
      },
      {
        id: 'coverage',
        items: [{ id: 'coverageWarning', kind: 'summary-coverage', requires: FLOW }],
      },
      {
        id: 'recent',
        items: [
          {
            id: 'recent',
            kind: 'summary-recent',
            status: 'preview',
            requires: ['records.read'],
          },
        ],
      },
    ],
  },
  flow: {
    first: [
      flow('received', 'flow-received', 'flow'),
      flow('missedCalls', 'flow-missed', 'missed'),
      flow('volume', 'flow-volume', 'flow'),
    ],
    sections: [
      {
        id: 'structure',
        items: [
          flow('categoryMix', 'flow-categories', 'flow'),
          flow('channels', 'flow-channels', 'flow'),
          flow('projects', 'flow-projects', 'flow'),
        ],
      },
      {
        id: 'load',
        items: [
          flow('hourly', 'flow-hours', 'flow'),
          flow('dayHour', 'flow-day-hour', 'flow'),
        ],
      },
    ],
  },
  sales: {
    tierFlag: 'enableDashboardSales',
    first: [
      sales('outcomes', 'sales-outcomes'),
      sales('crmFreshness', 'sales-freshness'),
    ],
    sections: [
      {
        id: 'outcomesQuality',
        items: [
          sales('outcomeScore', 'sales-outcome-score'),
          sales('stageAdvance', 'sales-stage-advance'),
        ],
      },
      {
        id: 'history',
        // The series funnel over every series, leads included (spec #1339, 6).
        items: [sales('callDepth', 'sales-depth', { report: 'flow' })],
      },
    ],
  },
  quality: {
    first: [
      { id: 'qualityScore', kind: 'quality-score', status: 'preview', requires: QUALITY },
      { id: 'qualityTrend', kind: 'quality-trend', status: 'preview', requires: QUALITY },
      quality('review', 'quality-review', {
        requires: ['aggregates.quality', 'records.read'],
        action: 'review',
      }),
      quality('skillsDrop', 'quality-skills-drop'),
    ],
    sections: [
      {
        id: 'skills',
        items: [
          quality('skills', 'quality-skills'),
          quality('script', 'quality-script', { action: 'setup' }),
          quality('missedSteps', 'quality-steps', { report: 'scriptSteps' }),
        ],
      },
      {
        id: 'objections',
        items: [
          // The section answers `noAccess` without the voice-of-customer
          // aggregates (the objections route's own gate), so the card is
          // offered only to those who have them.
          quality('objectionHandling', 'quality-objections', {
            requires: ['aggregates.quality', 'aggregates.voc'],
            report: 'objections',
          }),
        ],
      },
      {
        id: 'checks',
        items: [
          quality('complianceResults', 'quality-compliance'),
          quality('aiAccuracy', 'quality-ai-accuracy'),
          quality('attentionScore', 'quality-attention', { status: 'method' }),
        ],
      },
      {
        id: 'criteria',
        items: [quality('criteria', 'quality-criteria', { report: 'criteria' })],
      },
    ],
  },
  team: {
    first: [
      team('roster', 'team-roster'),
      team('attention', 'team-attention', { action: 'coaching' }),
      team('growth', 'team-growth'),
      {
        id: 'employeeIssues',
        kind: 'employee-issues',
        status: 'preview',
        requires: EMPLOYEE_ISSUES_TEAM,
      },
      {
        id: 'feedbackEffect',
        kind: 'feedback-effect',
        status: 'preview',
        requires: EMPLOYEE_ISSUES_TEAM,
      },
    ],
    sections: [
      { id: 'distribution', items: [team('distribution', 'team-distribution')] },
      { id: 'workload', items: [team('workload', 'team-workload')] },
      {
        id: 'coaching',
        items: [
          // Activity of notes and sessions: no team-wide source in the window
          // yet — the block `coachingActivity` comes with it (#1338).
          {
            id: 'coachingStats',
            kind: 'coaching-stats',
            status: 'preview',
            requires: ['aggregates.team', 'team.scores'],
            action: 'coaching',
            flag: 'enableCoaching',
          },
        ],
      },
    ],
  },
  voc: {
    tierFlag: 'enableVoiceOfCustomer',
    first: [
      voice('competitors', 'voice-competitors', 'voiceMarketing'),
      voice('complaints', 'voice-complaints', 'voiceProduct'),
      voice('marketObjections', 'voice-market-objections', 'objections'),
      voice('voiceTrends', 'voice-trends', 'voiceMarketing'),
    ],
    sections: [
      {
        id: 'topics',
        items: [
          voice('topics', 'voice-topics', 'voiceThemes'),
          voice('productFeedback', 'voice-product-feedback', 'voiceProduct'),
        ],
      },
      { id: 'needs', items: [voice('needs', 'voice-needs', 'voiceMarketing')] },
      {
        id: 'newAnalytics',
        items: [
          voice('questions', 'voice-questions', 'voiceQuestions', {
            reportRequires: VOICE_WORDS,
          }),
          voice('refusals', 'voice-refusals', 'voiceDeclines', {
            reportRequires: VOICE_WORDS,
          }),
        ],
      },
      // The tab's own export (E4, #1342): its labels and counts. The wordings'
      // export lives in the report, under the report's boundary.
      {
        id: 'export',
        items: [
          {
            id: 'voiceExport',
            kind: 'voice-export',
            status: 'preview',
            requires: VOC,
            report: 'voice',
          },
        ],
      },
    ],
  },
  // The employee's own view (#1338 PR C, rev 5 «Личный вид сотрудника»): one
  // «my score» — the E1 slice of their own records, the same answer the score,
  // the progress and the days read; the rest from `/agents/me/performance`.
  // «Сравнение с командой» (`competition`) is not drawn at all (decision
  // 12.08: the right to see colleagues' results is a decision of its own).
  mine: {
    first: [
      { id: 'myScore', kind: 'quality-score', status: 'preview', requires: QUALITY },
      personal('myProgress', 'personal-progress', { requires: QUALITY }),
      personal('myFocus', 'personal-focus'),
      personal('myRank', 'personal-rank'),
    ],
    sections: [
      { id: 'mySkills', items: [personal('mySkills', 'personal-skills')] },
      // The same dips the supervisor sees for this employee in «Команда» (#767).
      {
        id: 'myIssues',
        items: [
          { id: 'myIssues', kind: 'employee-issues', status: 'preview', requires: [] },
        ],
      },
      {
        id: 'myDays',
        items: [
          { id: 'myDays', kind: 'quality-trend', status: 'preview', requires: QUALITY },
        ],
      },
      { id: 'myBests', items: [personal('myBests', 'personal-bests')] },
      {
        id: 'myCoaching',
        items: [
          // My coaching meetings in the window (#2053).
          personal('personalCoaching', 'personal-coaching', { flag: 'enableCoaching' }),
          {
            id: 'myFeedbackEffect',
            kind: 'feedback-effect',
            status: 'preview',
            requires: [],
          },
          personal('supervisorNotes', 'personal-notes', { flag: 'enableCoaching' }),
        ],
      },
    ],
  },
};

/** The tab as the viewer may see it: its blocks and sections they may have. */
export function visibleLayout(tab: UnifiedSection, caps: CapabilitySet): TabLayout {
  const layout = UNIFIED_LAYOUT[tab];
  return {
    first: layout.first.filter((block) => meets(caps, block)),
    sections: layout.sections
      .map((section) => ({
        ...section,
        items: section.items.filter((block) => meets(caps, block)),
      }))
      .filter((section) => section.items.length > 0),
  };
}

/**
 * The tabs offered: those of the view with at least one block the viewer may
 * see, in order. The business view never offers «Моё», the personal view
 * nothing else.
 */
export function visibleSections(
  caps: CapabilitySet,
  view: UnifiedView = 'business'
): UnifiedSection[] {
  const tabs: readonly UnifiedSection[] =
    view === 'personal' ? PERSONAL_SECTIONS : BUSINESS_SECTIONS;
  return tabs.filter((tab) => {
    const layout = visibleLayout(tab, caps);
    return layout.first.length > 0 || layout.sections.length > 0;
  });
}

/** The tab on screen: the asked one when offered, else the first offered. */
export function activeSection(
  asked: UnifiedSection | undefined,
  visible: readonly UnifiedSection[]
): UnifiedSection | null {
  if (asked && visible.includes(asked)) return asked;
  return visible[0] ?? null;
}

/**
 * The tab is above the organization's plan by the flags' verdict — known
 * before it is opened, so a tab strip (the dashboard's own or the Bitrix24
 * host's) marks it locked. The tab's data route refuses by the same rule.
 */
export function sectionLockedByPlan(
  section: UnifiedSection,
  gate: (flag: FeatureFlagKey) => FeatureGateState
): boolean {
  const flag = UNIFIED_LAYOUT[section].tierFlag;
  return flag !== undefined && gate(flag) === 'tier_not_eligible';
}
